/**
 * Registro único de paths del shell privado v3 (UX dashboard on).
 * - Ampliar aquí al migrar pantallas; evita regex manual desincronizado.
 * - {@link matchesV3PrivateShellPath} para sagas, guards o links sin duplicar lógica.
 * - El `Route` padre del shell en web usa cada `path` de esta lista (RR 4.3 no acepta `path` RegExp).
 * - {@link getV3PrivateShellOuterPathPattern} queda solo para usos puntuales (p. ej. depuración); no usar como
 *   `path` de `<Route>`.
 */
import { matchPath } from "react-router-dom";

/** @typedef {{ path: string, exact?: boolean }} V3ShellPathMatcher */

/** @typedef {{ path: string, rule: 'manage' | 'list' }} V3FrequentShellPathRule */

/**
 * Destinos frecuentes bajo shell (permisos se resuelven en `v3PrivateShellRoutes.jsx`).
 * @type {V3FrequentShellPathRule[]}
 */
export const V3_PRIVATE_SHELL_FREQUENT_PATH_RULES = [
    { path: "/FrequentDestination/create/step1", rule: "manage" },
    { path: "/frequentDestination/create/:id/confirm", rule: "manage" },
    { path: "/frequentDestination/:id/modify/confirmation", rule: "manage" },
    { path: "/FrequentDestination/detail/:id/", rule: "manage" },
    { path: "/transfer/frequentDestination/:id/create/", rule: "manage" },
    { path: "/frequentDestination/:id/delete", rule: "manage" },
    { path: "/frequentDestination/:id/modify", rule: "manage" },
    { path: "/FrequentDestination", rule: "list" },
];

/**
 * Prefijos de URL que activan el shell (misma semántica que el antiguo regex manual:
 * entrada al layout unificado; el `Switch` interno resuelve pantalla o redirige a home).
 * `exact: false` coincide con subrutas sin falsear segmentos vecinos (p. ej. `/v2/transferDownload`).
 *
 * @type {V3ShellPathMatcher[]}
 */
export const V3_PRIVATE_SHELL_PATH_MATCHERS = [
    { path: "/v3/dashboard", exact: false },
    { path: "/accounts/:id", exact: true },
    { path: "/v2/accounts", exact: true },
    { path: "/v3/accounts", exact: true },
    { path: "/v3/accounts/:id/projectedBalance", exact: true },
    { path: "/v3/accounts/:id/historicBalance", exact: true },
    { path: "/v3/accounts/:id", exact: true },
    { path: "/v3/accounts/create", exact: true },
    { path: "/v3/accounts/edit", exact: true },
    { path: "/v3/accountOpening/summary", exact: true },
    { path: "/v3/account/create/:status/:id", exact: true },
    { path: "/accountOpening", exact: false },
    { path: "/account/proyectedBalance/:id", exact: true },
    { path: "/account/historicBalance/:id", exact: true },
    { path: "/account/:id/setProductAlias", exact: true },
    { path: "/ConsolidatedPosition", exact: true },
    { path: "/v2/agenda", exact: false },
    { path: "/v2/transfer", exact: false },
    { path: "/v3/transfer", exact: false },
    { path: "/v3/transfer/historic", exact: true },
    { path: "/v3/transfer/confirmation", exact: true },
    { path: "/v3/transfer/ticket", exact: true },
    { path: "/transfer/confirmation", exact: true },
    { path: "/transfer/cancelled", exact: true },
    { path: "/transfer/error", exact: true },
    { path: "/transfer/failed", exact: true },
    { path: "/transfer/pending", exact: true },
    { path: "/transfer/scheduled", exact: true },
    { path: "/transfer/scheduled/cancel", exact: true },
    { path: "/transfer/summary", exact: true },
    { path: "/transaction/:idTransaction", exact: false },
    { path: "/v3/credin", exact: false },
    { path: "/v3/requestCredin", exact: false },
    { path: "/requestCredin", exact: false },
    { path: "/v3/credin/ticket", exact: true },
    { path: "/confirmCredin/transaction", exact: true },
    { path: "/v2/transferDownload", exact: false },
    { path: "/v3/transfer/download", exact: true },
    { path: "/transfer/multiple", exact: false },
    { path: "/v2/corporateCard/accountStatements", exact: false },
    { path: "/pendingTransaction", exact: false },
    { path: "/externaltransactions", exact: false },
    { path: "/FrequentDestination", exact: false },
    { path: "/frequentDestination", exact: false },
    { path: "/transfer/frequentDestination", exact: false },
    { path: "/payments/payDebin", exact: false },
    { path: "/payments/recurrent", exact: false },
    { path: "/payments/recurrency", exact: false },
    { path: "/v2/debin/scheduled", exact: false },
    { path: "/v2/echeqs/control", exact: true },
    { path: "/v2/echeqs/mass-management", exact: false },
    { path: "/checks", exact: false },
    { path: "/checksDetail", exact: false },
    { path: "/discountedChecksDetail", exact: false },
    { path: "/v2/echeqRules", exact: false },
    { path: "/echeqRules", exact: false },
    { path: "/echeqs", exact: false },
    { path: "/v2/addlimitrequest", exact: true },
    { path: "/addlimitrequestconfirmation", exact: true },
    { path: "/addlimitrequest", exact: false },
    { path: "/confirmIncomingDebin/transaction", exact: true },
    { path: "/payThirdParties", exact: false },
    { path: "/v2/payThirdParties", exact: false },
    { path: "/transactions/list", exact: true },
    { path: "/transactions/list/filters", exact: true },
    { path: "/transactions/list/filters/results", exact: true },
    { path: "/transactions/cancel", exact: true },
    { path: "/v2/payments-services", exact: true },
    { path: "/v2/payAFIPSummary", exact: true },
    { path: "/servicePayments", exact: true },
    { path: "/servicePaymentsConfirmationTicket", exact: true },
    { path: "/agendaServicePaymentsConfirmationTicket", exact: true },
    { path: "/newPayment", exact: false },
    { path: "/adhesiones/list", exact: true },
    { path: "/agenda/payment", exact: true },
    { path: "/agenda/multiplePayment/detail/:key", exact: true },
    { path: "/payMultipleAFIPSummary", exact: true },
    { path: "/deletePaymentAFIPSummary", exact: true },
    { path: "/payPaymentAFIP", exact: true },
    { path: "/payPaymentAFIP/:idTransaction/ticket", exact: true },
    { path: "/payPaymentAFIPConfirmation", exact: true },
    { path: "/payPaymentAFIPSummary", exact: true },
    { path: "/payMultipleAFIPDetail", exact: true },
    { path: "/payMultipleAFIPSuccess", exact: true },
    { path: "/v2/payMultipleAFIP/success", exact: true },
    { path: "/multiplePaymentsConfirmation", exact: true },
    { path: "/multiplePaymentsSuccess", exact: true },
    { path: "/deletePaymentAFIP/:idTransaction/ticket", exact: true },
    { path: "/desadherir/confirmation", exact: true },
    { path: "/transactions/sign", exact: true },
    { path: "/v2/settings/hidebalance", exact: true },
    { path: "/payments/enabledContributors", exact: true },
    { path: "/fixedTermAcceptance", exact: false },
    { path: "/metrocorp", exact: true },
    { path: "/v3/metrocorp", exact: true },
    { path: "/deposits", exact: false },
    { path: "/depositsConstitute", exact: true },
    { path: "/v2/depositsConstitute", exact: true },
    { path: "/depositsSummary", exact: true },
    { path: "/depositsConfirmation", exact: false },
    { path: "/deposit", exact: false },
    { path: "/financing", exact: false },
    { path: "/ecomex", exact: false },
    { path: "/cedip", exact: false },
    { path: "/communications", exact: false },
    { path: "/v2/investment/currencyExchange", exact: false },
    { path: "/currencyExchangeConfirmation", exact: false },
    { path: "/fundInformation", exact: true },
    { path: "/v2/directDebit", exact: false },
    { path: "/directDebit", exact: false },
    { path: "/fundcorpOperations", exact: false },
    { path: "/subscribe", exact: false },
    { path: "/rescue", exact: false },
    { path: "/cancelSubscribe", exact: false },
    { path: "/registerShareAccount", exact: false },
    { path: "/accountsOfficer", exact: true },
    { path: "/settings", exact: false },
    { path: "/administration", exact: false },
    { path: "/charges", exact: false },
    { path: "/privacyPolicy", exact: true },
    { path: "/termsAndConditions", exact: true },
    { path: "/support", exact: true },
];

/**
 * Indica si la URL pertenece al shell v3 (mismas rutas que el `Switch` interno).
 * Útil en sagas (`getState().router.location.pathname`), helpers y componentes.
 *
 * @param {string|null|undefined} pathname
 * @returns {boolean}
 */
export function matchesV3PrivateShellPath(pathname) {
    if (pathname == null || typeof pathname !== "string") {
        return false;
    }
    return V3_PRIVATE_SHELL_PATH_MATCHERS.some((def) =>
        matchPath(pathname, { path: def.path, exact: def.exact === true }),
    );
}

let cachedOuterPathRegExp = null;

/**
 * Expresión regular equivalente a la unión de {@link V3_PRIVATE_SHELL_PATH_MATCHERS}.
 * No usar como `path` de `<Route>` en react-router-dom 4.3+ (solo acepta string).
 *
 * @returns {RegExp}
 */
export function getV3PrivateShellOuterPathPattern() {
    if (!cachedOuterPathRegExp) {
        cachedOuterPathRegExp = compileMatchersToOuterRegExp(V3_PRIVATE_SHELL_PATH_MATCHERS);
    }
    return cachedOuterPathRegExp;
}

/**
 * @param {V3ShellPathMatcher[]} matchers
 * @returns {RegExp}
 */
function compileMatchersToOuterRegExp(matchers) {
    const parts = matchers.map(({ path, exact }) => {
        const segments = path.split("/").filter((s) => s.length > 0);
        const body = segments
            .map((seg) => (seg.startsWith(":") ? "[^/]+" : seg.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")))
            .join("\\/");
        const withLeadingSlash = `\\/${body}`;
        if (exact === true) {
            return `${withLeadingSlash}(?:\\/)?`;
        }
        return `${withLeadingSlash}(?:\\/.*)?`;
    });
    return new RegExp(`^(?:${parts.join("|")})$`);
}
